import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, User, LogOut, Settings, Menu, X, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencyToggle from "@/components/CurrencyToggle";
import NavbarSearch from "@/components/NavbarSearch";

export default function Navbar() {
    const { user, logout, cartCount } = useAuth();
    const { content } = useSiteContent();
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const showBack = !["/", "/login"].includes(location.pathname);
    const goBack = () => (location.key !== "default" ? navigate(-1) : navigate("/"));

    const isApproved = user && user !== false && (user.role === "admin" || user.status === "approved");
    const isAdmin = user && user !== false && user.role === "admin";

    const navFontSize = content.nav_font_size || 12;
    const navWeight = content.nav_font_weight === "black" ? "900" : content.nav_font_weight === "normal" ? "500" : "700";
    const navCase = content.nav_uppercase === false ? "none" : "uppercase";

    const linkClass = ({ isActive }) =>
        `font-display tracking-wider px-3 py-2 transition-colors ${
            isActive ? "text-[hsl(var(--primary))]" : "text-slate-700 hover:text-[hsl(var(--primary))]"
        }`;
    const linkStyle = {
        fontSize: `${navFontSize}px`,
        fontWeight: navWeight,
        textTransform: navCase,
    };

    return (
        <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="navbar">
            {/* Top strip */}
            <div className="bg-slate-900 text-slate-300 text-xs font-mono">
                <div className="max-w-[1440px] mx-auto px-4 py-1.5 flex items-center justify-between gap-3">
                    <span className="hidden sm:inline truncate">
                        {content.top_banner}
                    </span>
                    <div className="flex items-center gap-3">
                        <CurrencyToggle />
                        <LanguageSwitcher compact />
                        <span className="hidden md:inline uppercase tracking-widest">
                            {content.top_banner_right}
                        </span>
                    </div>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4">
                <div className="flex items-center gap-6 h-16">
                    {showBack && (
                        <button
                            type="button"
                            onClick={goBack}
                            className="shrink-0 -mr-3 w-9 h-9 flex items-center justify-center border border-slate-300 text-slate-700 hover:border-slate-900 hover:text-[hsl(var(--primary))] transition-colors"
                            title="Kthehu mbrapa"
                            aria-label="Kthehu mbrapa"
                            data-testid="nav-back-button"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                    )}
                    {/* Logo */}
                    <Link to={isApproved ? "/" : "/login"} className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
                        {content.logo_url ? (
                            <img
                                src={content.logo_url}
                                alt={content.brand_name}
                                className="object-contain object-left w-auto max-w-[220px]"
                                style={{ height: `${content.navbar_logo_size || 36}px` }}
                                data-testid="nav-logo-image"
                            />
                        ) : (
                            <span className="font-display font-black text-base uppercase tracking-tight" data-testid="nav-logo-text">
                                {content.brand_name}
                            </span>
                        )}
                    </Link>

                    {/* Search — inline form matching homepage Quick Search */}
                    {isApproved && <NavbarSearch compact />}

                    {/* Nav */}
                    {isApproved && (
                        <nav className="hidden lg:flex items-center gap-1 ml-auto">
                            <NavLink to="/catalog" className={linkClass} style={linkStyle} data-testid="nav-catalog">
                                {t("nav.catalog")}
                            </NavLink>
                            <NavLink to="/new" className={linkClass} style={linkStyle} data-testid="nav-new">
                                {t("nav.new")}
                            </NavLink>
                        </nav>
                    )}

                    <div className={`flex items-center gap-3 ${isApproved ? "" : "ml-auto"}`}>
                        {isAdmin && (
                            <Link
                                to="/admin"
                                data-testid="nav-admin"
                                className="hidden md:inline-flex items-center gap-1.5 font-display tracking-wider text-slate-700 hover:text-[hsl(var(--primary))]"
                                style={linkStyle}
                            >
                                <Settings className="w-4 h-4" /> {t("nav.admin")}
                            </Link>
                        )}

                        {isApproved && (
                            <Link
                                to="/cart"
                                data-testid="nav-cart"
                                className="relative p-2 hover:text-[hsl(var(--primary))]"
                            >
                                <ShoppingCart className="w-5 h-5" />
                                {cartCount > 0 && (
                                    <span
                                        className="absolute -top-0.5 -right-0.5 bg-[hsl(var(--primary))] text-white text-[10px] font-mono font-bold w-4 h-4 flex items-center justify-center"
                                        data-testid="cart-count-badge"
                                    >
                                        {cartCount}
                                    </span>
                                )}
                            </Link>
                        )}

                        {user && user !== false ? (
                            <div className="hidden md:flex items-center gap-2">
                                <div className="text-right leading-tight">
                                    <div className="text-xs font-mono font-bold truncate max-w-[160px]" data-testid="nav-user-email">
                                        {user.email}
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-500">
                                        {user.role === "admin" ? "admin" : user.status || "pending"}
                                    </div>
                                </div>
                                <button
                                    data-testid="nav-logout"
                                    onClick={logout}
                                    className="p-2 hover:text-[hsl(var(--primary))]"
                                    title={t("nav.logout")}
                                >
                                    <LogOut className="w-5 h-5" />
                                </button>
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                data-testid="nav-login"
                                className="hidden md:inline-flex items-center gap-1.5 font-display tracking-wider text-slate-700 hover:text-[hsl(var(--primary))]"
                                style={linkStyle}
                            >
                                <User className="w-4 h-4" /> {t("nav.login")}
                            </Link>
                        )}

                        <button
                            className="lg:hidden p-2"
                            onClick={() => setMenuOpen(!menuOpen)}
                            data-testid="nav-menu-toggle"
                        >
                            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile */}
                {menuOpen && (
                    <div className="lg:hidden border-t border-slate-200 py-4 space-y-3">
                        {isApproved && (
                            <NavbarSearch compact={false} />
                        )}
                        <div className="flex flex-col">
                            {isApproved && (
                                <>
                                    <NavLink to="/catalog" className={linkClass} style={linkStyle} onClick={() => setMenuOpen(false)}>
                                        {t("nav.catalog")}
                                    </NavLink>
                                    <NavLink to="/new" className={linkClass} style={linkStyle} onClick={() => setMenuOpen(false)}>
                                        {t("nav.new")}
                                    </NavLink>
                                </>
                            )}
                            {isAdmin && (
                                <NavLink to="/admin" className={linkClass} style={linkStyle} onClick={() => setMenuOpen(false)}>
                                    {t("nav.admin")}
                                </NavLink>
                            )}
                            {user && user !== false ? (
                                <button
                                    onClick={() => {
                                        logout();
                                        setMenuOpen(false);
                                    }}
                                    className="font-display tracking-wider px-3 py-2 text-left text-slate-700"
                                    style={linkStyle}
                                >
                                    {t("nav.logout")}
                                </button>
                            ) : (
                                <NavLink to="/login" className={linkClass} style={linkStyle} onClick={() => setMenuOpen(false)}>
                                    {t("nav.login")}
                                </NavLink>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
}
